import ChartModal from '@/components/ChartModal';
import Header from '@/components/Header';
import { TooltipProvider } from '@/components/ui/tooltip';
import { supabase } from '@/lib/supabase';
import axios from 'axios';
import { Trophy } from 'lucide-react';
import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

import AllTimeArtistRankings from '@/components/rankings/AllTimeArtistRankings';
// Import the new components
import AllTimeSongRankings from '@/components/rankings/AllTimeSongRankings';

// Import types
import type {
  AllTimeArtistRankingResponse,
  AllTimeRankingResponse,
  ArtistRankingItem,
  MonthlyRankingResponse,
  RankingItem,
  YearResponse,
} from '@/types/rankings';

// Import slick-carousel styles
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

const ITEMS_PER_PAGE = 10;
const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

// Google Identity Services 타입 정의
interface GoogleTokenResponse {
  access_token: string;
  expires_in: number;
  scope: string;
  token_type: string;
  error?: string;
}

interface GoogleTokenClient {
  requestAccessToken: (overrideConfig?: { prompt?: string }) => void;
}

interface GoogleOAuth2 {
  initTokenClient: (config: {
    client_id: string;
    scope: string;
    callback: (response: GoogleTokenResponse) => void;
    error_callback?: (error: unknown) => void;
    enable_granular_consent?: boolean;
  }) => GoogleTokenClient;
}

interface GoogleAccounts {
  oauth2: GoogleOAuth2;
}

interface GoogleIdentityServices {
  accounts: GoogleAccounts;
}

declare global {
  interface Window {
    google?: GoogleIdentityServices;
  }
}

const MOBILE_BREAKPOINT = 768;

const RankingPage: React.FC = () => {
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [allTimeRankings, setAllTimeRankings] = useState<RankingItem[]>([]);
  const [monthlyRankings, setMonthlyRankings] = useState<Record<number, RankingItem[]>>({}); // Month number (1-12) -> RankingItem[]
  const [loadingAllTime, setLoadingAllTime] = useState<boolean>(true);
  const [loadingMonthly, setLoadingMonthly] = useState<boolean>(false);
  const [availableYears, setAvailableYears] = useState<string[]>([]); // State to hold distinct years
  const [selectedYear, setSelectedYear] = useState<string>('');
  const [errorAllTime, setErrorAllTime] = useState<string | null>(null);
  const [errorMonthly, setErrorMonthly] = useState<string | null>(null);
  const [itemsToShow, setItemsToShow] = useState<number>(ITEMS_PER_PAGE);
  const [isChartModalOpen, setIsChartModalOpen] = useState(false);
  const [selectedChartItem, setSelectedChartItem] = useState<{
    title: string;
    artist: string;
  } | null>(null);
  const [isDragging, setIsDragging] = useState(false); // State to track slider dragging

  // --- State for All-Time Artist Rankings ---
  const [allTimeArtistRankings, setAllTimeArtistRankings] = useState<ArtistRankingItem[]>([]);
  const [loadingAllTimeArtists, setLoadingAllTimeArtists] = useState<boolean>(true);
  const [errorAllTimeArtists, setErrorAllTimeArtists] = useState<string | null>(null);
  // --- End of State ---

  // State for Playlist Creation
  const [isCreatingPlaylist, setIsCreatingPlaylist] = useState(false);
  const [googleAccessToken, setGoogleAccessToken] = useState<string | null>(null);
  const googleTokenClient = useRef<GoogleTokenClient | null>(null);
  const isMounted = useRef(true); // Add isMounted ref

  // Add state for mobile detection
  const [isMobile, setIsMobile] = useState(window.innerWidth < MOBILE_BREAKPOINT);

  // State for year fetching
  const [loadingYears, setLoadingYears] = useState<boolean>(false);
  const [errorYears, setErrorYears] = useState<string | null>(null);

  // Effect for mobile detection on resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    };
    window.addEventListener('resize', handleResize);
    // Initial check in case window size changed before listener attached
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Check if component is mounted
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Load saved date from localStorage on initial render
  useEffect(() => {
    const savedDate = localStorage.getItem('selectedDate');
    if (savedDate) {
      setSelectedDate(savedDate);
    }
    // Fetch all-time song rankings
    fetchAllTimeRankingsData().catch((error) => {
      console.error('Error fetching all-time song rankings on initial load:', error);
      setErrorAllTime('Failed to load initial song ranking data.');
      setLoadingAllTime(false);
    });
    // Fetch all-time artist rankings
    fetchAllTimeArtistRankingsData().catch((error) => {
      console.error('Error fetching all-time artist rankings on initial load:', error);
      setErrorAllTimeArtists('Failed to load initial artist ranking data.');
      setLoadingAllTimeArtists(false);
    });
  }, []);

  const fetchInitialData = async () => {
    // setLoadingAllTime(true); // Loading state is now primarily for all-time
    setErrorAllTime(null);
    setErrorAllTimeArtists(null); // Reset artist error too
    setAvailableYears([]);
    try {
      // Fetch both song and artist rankings
      await Promise.all([fetchAllTimeRankingsData(), fetchAllTimeArtistRankingsData()]);
    } catch (error) {
      console.error('Error fetching initial data:', error);
      // Keep error state related to all-time data
      setErrorAllTime('Failed to load initial ranking data. Please try again later.');
      // Set artist error as well if needed, or rely on individual fetch errors
    }
  };

  // --- Handle fetching years ---
  const handleFetchYears = async () => {
    setLoadingYears(true);
    setErrorYears(null);
    setAvailableYears([]); // Clear existing years
    console.log('Attempting to fetch distinct years manually...');
    try {
      const { data: yearData, error: yearError } = await supabase.rpc(
        'get_distinct_broadcast_years',
        {}
      );

      console.log('Manual fetch: Distinct year data:', yearData);
      console.log('Manual fetch: Year error:', yearError);

      if (yearError) {
        // Check for specific 404 error
        if (yearError.message.includes('404')) {
          throw new Error(
            "Function 'get_distinct_broadcast_years' not found (404). Please check Supabase setup."
          );
        }
        throw yearError; // Re-throw other errors
      }

      const currentYear = new Date().getFullYear();
      const years =
        yearData
          ?.map((item: YearResponse) => item.year)
          .filter((year: number) => year <= currentYear + 1) // Keep filtering logic
          .sort((a: number, b: number) => b - a) // Sort years descending, add types
          .map((year: number) => year.toString()) || [];

      console.log('Manual fetch: Processed years:', years);
      setAvailableYears(years);
      if (years.length === 0) {
        setErrorYears('No distinct years found from the database.');
      }
    } catch (error: unknown) {
      console.error('Error manually fetching distinct years:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      setErrorYears(`Failed to load years: ${errorMessage}`);
      setAvailableYears([]); // Ensure years are cleared on error
    } finally {
      setLoadingYears(false);
    }
  };
  // --- End of years fetch handler ---

  const fetchAllTimeRankingsData = async (): Promise<void> => {
    setLoadingAllTime(true);
    setErrorAllTime(null);
    try {
      const { data, error } = await supabase.rpc('get_all_time_rankings', {
        year_filter: null, // Always fetch all-time regardless of selected year
        p_limit: 100, // Fetch top 100 songs (grouped by UPPER(artist) and UPPER(title) for case-insensitive matching)
      });

      console.log('All-time song rankings data:', data);
      console.log('All-time song rankings error:', error);

      if (error) {
        // Check for specific 404 error for the function
        if (
          error.message.includes('404') ||
          error.message.includes('does not exist') ||
          (error.message.includes('function') && error.message.includes('not found'))
        ) {
          throw new Error(
            "Function 'get_all_time_rankings' not found (404). Please update the function in Supabase with the new signature: get_all_time_rankings(year_filter integer, p_limit integer)."
          );
        }
        if (error.message.includes('argument') || error.message.includes('parameter')) {
          throw new Error(
            `Function parameter error: ${error.message}. Please ensure the function signature matches: get_all_time_rankings(year_filter integer, p_limit integer).`
          );
        }
        throw error;
      }

      // Map the response data to RankingItem format
      // Note: The SQL function groups by UPPER(artist) and UPPER(title) to handle case variations
      // (e.g., "The Beatles" = "THE BEATLES", "Hey Jude" = "HEY JUDE")
      const rankings =
        data?.map((item: AllTimeRankingResponse) => ({
          artist: item.artist,
          title: item.title,
          count: Number(item.count),
        })) || [];

      setAllTimeRankings(rankings);
      console.log('All-time song rankings fetched, count:', rankings.length);
      console.log(
        'All-time song rankings fetched successfully (case-insensitive grouping applied)'
      );
    } catch (error: unknown) {
      console.error('Error fetching all-time song rankings:', error);
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to load all-time song ranking data.';
      setErrorAllTime(errorMessage);
      toast.error(`랭킹 데이터 로드 실패: ${errorMessage}`);
    } finally {
      setLoadingAllTime(false);
    }
  };

  // --- Fetch All-Time Artist Rankings ---
  // Note: The SQL function groups by UPPER(artist) to handle case variations
  // (e.g., "The Beatles" = "THE BEATLES" = "the beatles")
  const fetchAllTimeArtistRankingsData = async (): Promise<void> => {
    setLoadingAllTimeArtists(true);
    setErrorAllTimeArtists(null);
    try {
      const { data, error } = await supabase.rpc('get_all_time_artist_rankings', {
        p_limit: 100, // Fetch top 100 artists (grouped by UPPER(artist) for case-insensitive matching)
      });

      console.log('All-time artist rankings data:', data);
      if (error) {
        // Check for specific 404 error for the new function
        if (error.message.includes('404') || error.message.includes('does not exist')) {
          throw new Error(
            "Function 'get_all_time_artist_rankings' not found (404). Please create it in Supabase."
          );
        }
        throw error; // Re-throw other errors
      }

      // Map the response data to ArtistRankingItem format
      // Note: The SQL function groups by UPPER(artist) to handle case variations
      const rankings =
        data?.map((item: AllTimeArtistRankingResponse) => ({
          artist: item.artist,
          count: Number(item.count),
        })) || [];

      setAllTimeArtistRankings(rankings);
      console.log('All-time artist rankings fetched, count:', rankings.length);
      console.log(
        'All-time artist rankings fetched successfully (case-insensitive grouping applied)'
      );
    } catch (error: unknown) {
      console.error('Error fetching all-time artist rankings:', error);
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to load artist ranking data';
      setErrorAllTimeArtists(errorMessage);
    } finally {
      setLoadingAllTimeArtists(false);
    }
  };
  // --- End Fetch All-Time Artist Rankings ---

  const fetchMonthlyRankingsData = useCallback(async (year: string): Promise<void> => {
    if (!year) {
      setMonthlyRankings({});
      return;
    }

    setLoadingMonthly(true);
    setErrorMonthly(null);
    setMonthlyRankings({}); // Clear previous year's data

    try {
      // Assume RPC 'get_monthly_rankings' exists and takes 'year_filter' (INT)
      // It should return { month: number, artist: string, title: string, count: number }
      // ordered by count desc within each month, limited to top 5.
      const { data, error } = await supabase.rpc('get_monthly_rankings', {
        year_filter: Number.parseInt(year),
      });

      console.log(`RPC Response for get_monthly_rankings (${year}):`, { data, error }); // Log raw response

      if (error) throw error;

      if (!data) {
        console.log(`No monthly ranking data found for ${year}`);
        setMonthlyRankings({}); // Ensure it's empty if no data
        return;
      }

      // Process the data into the desired format: Record<number, RankingItem[]>
      const processedRankings: Record<number, RankingItem[]> = {};
      for (const item of data) {
        const month = item.month; // Assuming month is 1-12
        if (!processedRankings[month]) {
          processedRankings[month] = [];
        }
        // The RPC should already limit to top 5, but check just in case
        if (processedRankings[month].length < 5) {
          processedRankings[month].push({
            artist: item.artist,
            title: item.title,
            count: Number(item.count),
          });
        }
      }

      console.log(`Processed Monthly Rankings for ${year}:`, processedRankings); // Log processed data
      setMonthlyRankings(processedRankings);
      console.log(`Monthly rankings for ${year} fetched successfully`);
    } catch (error) {
      console.error(`Error fetching monthly rankings for ${year}:`, error);
      setErrorMonthly(`Failed to load monthly ranking data for ${year}.`);
      setMonthlyRankings({}); // Clear on error
    } finally {
      setLoadingMonthly(false);
    }
  }, []);

  // Fetch monthly data when selected year changes
  useEffect(() => {
    // Only fetch monthly data if a year is selected AND the initial all-time load is complete.
    // This prevents fetching during the initial setup and handles subsequent year changes.
    // Check loading for both all-time songs and artists to avoid conflicts?
    // Let's stick to loadingAllTime (songs) for now, assuming it's the primary trigger.
    if (selectedYear && !loadingAllTime) {
      // Check if loadingAllTime is FALSE
      fetchMonthlyRankingsData(selectedYear);
    }
    // We depend on selectedYear changing OR loadingAllTime finishing (transitioning to false)
    // after selectedYear has been initially set.
  }, [selectedYear, loadingAllTime, fetchMonthlyRankingsData]); // Add fetchMonthlyRankingsData dependency

  const handleDateChange = (date: string) => {
    setSelectedDate(date);
    localStorage.setItem('selectedDate', date);
  };

  const handleSearchResultClick = (title: string, artist: string) => {
    setSelectedChartItem({ title, artist });
    setIsChartModalOpen(true);
  };

  const handleCloseChartModal = () => {
    setIsChartModalOpen(false);
    setSelectedChartItem(null);
  };

  const handleYearChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const year = e.target.value;
    setSelectedYear(year);
    setItemsToShow(ITEMS_PER_PAGE); // Reset items to show for all-time list when year changes
    // Monthly data fetching is handled by the useEffect watching selectedYear
  };

  // --- Playlist Creation Logic (Adapted from MusicTable.tsx) ---

  // Helper function using fetch as provided by the user
  const createPlaylistInternal = useCallback(
    async (
      accessToken: string,
      title: string,
      videoIds: string[]
    ): Promise<{ success: boolean; playlistId?: string; error?: string }> => {
      let playlistId: string | null = null;
      try {
        // 1. 플레이리스트 생성
        const playlistRes = await fetch(
          'https://www.googleapis.com/youtube/v3/playlists?part=snippet,status',
          {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${accessToken}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              snippet: {
                title,
                description: 'Generated by My Radio App - All Time Top 100 Songs', // Description updated
              },
              status: {
                privacyStatus: 'private', // or 'public', 'unlisted'
              },
            }),
          }
        );

        if (!playlistRes.ok) {
          const errorData = await playlistRes.json();
          console.error('Error creating playlist:', errorData);
          throw new Error(
            `Playlist creation failed: ${errorData.error?.message || playlistRes.statusText}`
          );
        }

        const playlist = await playlistRes.json();
        playlistId = playlist.id;
        console.log(`Playlist created: ID = ${playlistId}`);

        if (!playlistId) {
          throw new Error('Failed to get playlist ID after creation.');
        }

        // 2. 각 영상 추가 (실패해도 계속 진행)
        let addedCount = 0;
        let failedCount = 0;
        for (const videoId of videoIds) {
          try {
            const itemRes = await fetch(
              'https://www.googleapis.com/youtube/v3/playlistItems?part=snippet',
              {
                method: 'POST',
                headers: {
                  Authorization: `Bearer ${accessToken}`,
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                  snippet: {
                    playlistId: playlistId,
                    resourceId: {
                      kind: 'youtube#video',
                      videoId,
                    },
                  },
                }),
              }
            );

            if (!itemRes.ok) {
              const errorData = await itemRes.json();
              console.warn(`Failed to add video ${videoId}:`, errorData);
              failedCount++;
            } else {
              await itemRes.json(); // Process response if needed
              console.log(`Added video ${videoId} to playlist ${playlistId}`);
              addedCount++;
            }
          } catch (itemError) {
            console.warn(`Error adding video ${videoId}:`, itemError);
            failedCount++;
          }
        }

        console.log(`Finished adding videos. Added: ${addedCount}, Failed: ${failedCount}`);
        return { success: true, playlistId };
      } catch (error: unknown) {
        console.error('Error in createPlaylistInternal:', error);
        const errorMessage =
          error instanceof Error
            ? error.message
            : 'Unknown error during playlist creation/adding videos.';
        return { success: false, error: errorMessage };
      }
    },
    []
  );

  // YouTube 비디오 검색 함수
  const searchYoutubeVideo = useCallback(async (query: string): Promise<string | null> => {
    const apiKey = import.meta.env.VITE_GOOGLE_API_KEY;
    if (!apiKey) {
      console.error('VITE_GOOGLE_API_KEY is not set.');
      // Use toast for user feedback instead of setError state
      toast.error('Google API 키가 설정되지 않아 YouTube 검색을 할 수 없습니다.');
      return null;
    }

    try {
      const response = await axios.get('https://www.googleapis.com/youtube/v3/search', {
        params: {
          part: 'snippet',
          q: query,
          key: apiKey,
          maxResults: 1,
          type: 'video',
        },
      });

      if (response.data.items && response.data.items.length > 0) {
        return response.data.items[0].id.videoId;
      }
      console.warn(`No video found for query: ${query}`);
      return null;
    } catch (error: unknown) {
      const errorMessage = axios.isAxiosError(error)
        ? error.response?.data || error.message
        : error instanceof Error
          ? error.message
          : 'Unknown error';
      console.error(`Error searching YouTube video for query "${query}":`, errorMessage);
      // 검색 실패 시 에러를 전파하지 않고 null 반환 (개별 곡 실패 처리)
      return null;
    }
  }, []); // No dependencies needed if VITE_GOOGLE_API_KEY doesn't change

  // 실제 YouTube 플레이리스트 생성 및 노래 추가 로직 (Toast 사용)
  const createYoutubePlaylist = useCallback(
    async (accessToken: string) => {
      const playlistItems = allTimeRankings; // Use the fetched top 100 song rankings
      console.log(
        `[createYoutubePlaylist] Starting for All-Time Top ${playlistItems.length} Songs`
      );

      setIsCreatingPlaylist(true);
      setErrorAllTime(null); // Clear previous errors related to song ranking fetching

      let toastId: string | number | undefined = undefined; // Initialize toastId

      try {
        if (playlistItems.length === 0) {
          console.warn('[createYoutubePlaylist] No song ranking data available.');
          toast.warning('노래 랭킹 데이터가 없습니다.');
          setIsCreatingPlaylist(false);
          return;
        }

        const playlistName = `My Radio - All Time Top ${playlistItems.length} Songs`;

        // Proceed with YouTube search using the full playlistData
        toastId = toast.loading(`[1/${playlistItems.length}] YouTube 영상 검색 중...`);
        console.log(
          `[createYoutubePlaylist] Starting YouTube search for ${playlistItems.length} items.`
        );

        const searchPromises = playlistItems.map(async (item, index) => {
          const query = `${item.artist} ${item.title}`;
          const videoId = await searchYoutubeVideo(query);
          toast.loading(`[${index + 1}/${playlistItems.length}] YouTube 영상 검색 중...`, {
            id: toastId,
          });
          if (!videoId) {
            console.warn(`[createYoutubePlaylist] Video not found for: ${query}`);
          }
          return videoId;
        });

        const videoIdResults = await Promise.all(searchPromises);
        const validVideoIds = videoIdResults.filter((id): id is string => id !== null);

        const foundCount = validVideoIds.length;
        const notFoundCount = playlistItems.length - foundCount;
        console.log(
          `[createYoutubePlaylist] YouTube search complete. Found: ${foundCount}, Not Found: ${notFoundCount}`
        );

        if (validVideoIds.length === 0) {
          const errorMsg = `YouTube 영상을 찾지 못했습니다. 플레이리스트를 생성할 수 없습니다.${notFoundCount > 0 ? ` (${notFoundCount}개 검색 실패)` : ''}`;
          toast.error(errorMsg, { id: toastId });
          setIsCreatingPlaylist(false);
          return;
        }

        toast.loading(`'${playlistName}' 플레이리스트 생성 및 ${foundCount}곡 추가 시작...`, {
          id: toastId,
        });
        console.log(
          `[createYoutubePlaylist] Calling createPlaylistInternal with ${validVideoIds.length} video IDs.`
        );

        const result = await createPlaylistInternal(accessToken, playlistName, validVideoIds);

        if (result.success && result.playlistId) {
          console.log(
            `[createYoutubePlaylist] Playlist creation successful. ID: ${result.playlistId}`
          );
          const successMessage = `총 ${foundCount}개 영상 검색됨.${notFoundCount > 0 ? ` (${notFoundCount}곡 검색 실패)` : ''}`;
          const playlistUrl = `https://www.youtube.com/playlist?list=${result.playlistId}`;

          toast.success(`플레이리스트 '${playlistName}' 생성 완료!`, {
            id: toastId,
            description: successMessage,
            duration: 10000, // Show success longer
            action: {
              label: '플레이리스트 보기',
              onClick: () => window.open(playlistUrl, '_blank'),
            },
          });
        } else {
          console.error('[createYoutubePlaylist] Playlist creation failed:', result.error);
          const errorMessage = result.error || '알 수 없는 오류 발생';
          toast.error('플레이리스트 생성 실패', {
            id: toastId,
            description: errorMessage,
            duration: 10000, // Show error longer
          });
        }
      } catch (error) {
        // Catch any unexpected errors during the process
        console.error('[createYoutubePlaylist] Unexpected error:', error);
        const errorMsg = error instanceof Error ? error.message : '예상치 못한 오류 발생';
        if (toastId) {
          toast.error('플레이리스트 생성 중 예상치 못한 오류', {
            id: toastId,
            description: errorMsg,
          });
        } else {
          toast.error(`플레이리스트 생성 중 예상치 못한 오류: ${errorMsg}`);
        }
      } finally {
        // Ensure loading state is always turned off
        setIsCreatingPlaylist(false);
      }
    },
    [allTimeRankings, searchYoutubeVideo, createPlaylistInternal]
  ); // Depend on rankings, search function, and createPlaylistInternal

  // Google Identity Services 초기화
  useEffect(() => {
    const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
    if (!clientId) {
      console.error('VITE_GOOGLE_CLIENT_ID is not set in .env file.');
      toast.error('Google 인증 설정 오류: 클라이언트 ID가 없습니다.');
      return;
    }

    let timeoutId: NodeJS.Timeout | null = null;
    let isClientInitialized = false;

    const initializeClient = () => {
      if (isClientInitialized || !isMounted.current) return; // 마운트 상태 확인 추가

      if (window.google?.accounts?.oauth2?.initTokenClient) {
        try {
          googleTokenClient.current = window.google.accounts.oauth2.initTokenClient({
            client_id: clientId,
            scope: 'https://www.googleapis.com/auth/youtube.force-ssl',
            callback: (tokenResponse: GoogleTokenResponse) => {
              console.log('[GSI Callback] Received response:', tokenResponse);
              if (tokenResponse?.access_token) {
                console.log('[GSI Callback] Google Access Token received successfully.');
                setGoogleAccessToken(tokenResponse.access_token);
                console.log('[GSI Callback] Calling createYoutubePlaylist...');
                // NOTE: This callback might need context if we want playlist creation for artists later
                createYoutubePlaylist(tokenResponse.access_token);
              } else {
                console.error('[GSI Callback] Failed to get Google Access Token:', tokenResponse);
                toast.error('Google 인증 실패: 액세스 토큰을 얻을 수 없습니다.');
                setIsCreatingPlaylist(false);
              }
            },
            error_callback: (error: unknown) => {
              console.error('Google Auth Error (callback):', error);

              // Handle unverified app warning - provide helpful guidance
              const errorObj =
                error && typeof error === 'object' && 'type' in error
                  ? (error as { type?: string; message?: string })
                  : null;

              if (errorObj?.type === 'popup_closed' || errorObj?.type === 'popup_blocked') {
                toast.error('팝업이 차단되었습니다. 브라우저 설정에서 팝업을 허용해주세요.');
              } else if (errorObj?.type === 'access_denied') {
                toast.error(
                  '인증이 취소되었습니다. 플레이리스트 생성을 위해 Google 인증이 필요합니다.'
                );
              } else {
                const errorMessage = errorObj?.message || '알 수 없는 오류';
                toast.error(`Google 인증 오류: ${errorMessage}`);
              }
              setIsCreatingPlaylist(false);
            },
            // Enable granular consent for better user experience
            enable_granular_consent: true,
          });
          isClientInitialized = true;
          console.log('Google Token Client Initialized Successfully.');
        } catch (initError) {
          console.error('Error initializing Google Token Client:', initError);
          toast.error('Google 인증 클라이언트 초기화 중 오류 발생.');
          isClientInitialized = false; // 초기화 실패 상태 명시
        }
      } else {
        console.error('Google Identity Services client structure not ready or function missing.');
        toast.error(
          'Google 인증 라이브러리 구조가 예상과 다릅니다. 페이지 새로고침이 필요할 수 있습니다.'
        );
        isClientInitialized = false;
      }
    };

    const gsiScript = document.querySelector(
      'script[src="https://accounts.google.com/gsi/client"]'
    );

    const onScriptLoad = () => {
      console.log("GSI script 'load' event detected. Initializing client...");
      if (timeoutId) clearTimeout(timeoutId);
      initializeClient();
    };

    if (window.google?.accounts?.oauth2?.initTokenClient) {
      console.log('GSI client already available. Initializing immediately.');
      initializeClient();
    } else if (gsiScript) {
      console.log('GSI script found, adding load listener and setting timeout fallback...');
      gsiScript.addEventListener('load', onScriptLoad);
      gsiScript.addEventListener('error', () => {
        console.error('Failed to load GSI script.');
        toast.error('Google 인증 스크립트 로딩 실패.');
        if (timeoutId) clearTimeout(timeoutId);
      });
      timeoutId = setTimeout(() => {
        console.warn('GSI script load timeout reached. Attempting initialization anyway...');
        gsiScript.removeEventListener('load', onScriptLoad);
        initializeClient();
      }, 3000);
    } else {
      console.error('GSI script tag not found in the document.');
      toast.error('Google 인증 스크립트가 페이지에 없습니다. index.html 파일을 확인하세요.');
    }

    return () => {
      console.log('Cleaning up GSI initialization useEffect.');
      if (gsiScript) {
        gsiScript.removeEventListener('load', onScriptLoad);
      }
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [createYoutubePlaylist]); // createYoutubePlaylist 의존성 추가

  // This handler is currently tied to the Song playlist button
  const handleCreatePlaylist = useCallback(async () => {
    setIsCreatingPlaylist(true);

    if (!googleTokenClient.current) {
      console.error('Create button clicked, but Google client is not initialized yet.');
      toast.error('Google 인증 클라이언트가 아직 준비되지 않았습니다. 잠시 후 다시 시도해주세요.');
      setIsCreatingPlaylist(false);
      return;
    }

    if (googleAccessToken) {
      console.log('Using existing Google Access Token for song playlist creation.');
      await createYoutubePlaylist(googleAccessToken); // Calls the song playlist creator
    } else {
      console.log('Requesting Google Access Token for song playlist creation...');
      try {
        // The callback in initTokenClient will handle calling createYoutubePlaylist
        googleTokenClient.current.requestAccessToken({ prompt: 'consent' });
      } catch (requestError) {
        console.error('Error requesting Google Access Token:', requestError);
        toast.error('Google 인증 토큰 요청 중 오류 발생.');
        setIsCreatingPlaylist(false);
      }
    }
  }, [googleAccessToken, createYoutubePlaylist]);

  // --- End of Playlist Creation Logic ---

  return (
    <div className="min-h-screen">
      <Header onDateChange={handleDateChange} onSearchResultClick={handleSearchResultClick} />

      <main className="container mx-auto max-w-7xl px-2 sm:px-4 py-8">
        <TooltipProvider>
          {' '}
          {/* Added TooltipProvider here to wrap sections using tooltips */}
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold mb-2 flex items-center justify-center gap-2 text-white">
              <Trophy className="h-7 w-7 text-primary" />
              Ranking
            </h1>
            <p className="text-gray-400">Browse top ranked music and artists</p>
          </div>
          {/* All-Time Song Rankings Component */}
          <AllTimeSongRankings
            rankings={allTimeRankings}
            loading={loadingAllTime}
            error={errorAllTime}
            isDragging={isDragging}
            setIsDragging={setIsDragging}
            isMobile={isMobile}
            handleSearchResultClick={handleSearchResultClick}
            handleCreatePlaylist={handleCreatePlaylist}
            isCreatingPlaylist={isCreatingPlaylist}
          />
          {/* All-Time Artist Rankings Component */}
          <AllTimeArtistRankings
            rankings={allTimeArtistRankings}
            loading={loadingAllTimeArtists}
            error={errorAllTimeArtists}
            isDragging={isDragging}
            setIsDragging={setIsDragging}
            isMobile={isMobile}
          />
        </TooltipProvider>{' '}
        {/* Closing TooltipProvider */}
      </main>

      {/* Render ChartModal */}
      {selectedChartItem && (
        <ChartModal
          isOpen={isChartModalOpen}
          onClose={handleCloseChartModal}
          title={selectedChartItem.title}
          artist={selectedChartItem.artist}
        />
      )}
    </div>
  );
};

export default RankingPage;
